'use client';
import React, { createContext, useCallback, useEffect, useState } from 'react';
import { AuthData } from 'auth-types';
import { usePathname, useRouter } from 'next/navigation';
import { role } from 'auth-types';
import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { EffectiveRole } from 'auth-types';
import { isPathPermittedForRole, resolveFirstPermittedRoute } from '@/services/nav/nav-item-map';
import UserCourseStorageService from '@/services/storage/user-course-storage-service';
import {
  setEffectiveRole,
  setisLoggedIn,
  setRole,
  setActiveView,
  setUserEmail,
  setUserFirstName,
  setUserLastName,
} from '@/lib/slices/login-slice';
import AuthService from '@/services/auth/auth-service';
import Request from '@/services/util/request-service';
import EffectiveRoleService from '@/services/auth/effective-role-service';
import HomeViewAccessService from '@/services/auth/home-view-access-service';
import ErrorLoggerService from '@/services/util/error-logger-service';

type AuthContextType = {
  authData: AuthData;
  setAuthData: (data: any) => void;
};

const defaultValue: AuthContextType = {
  authData: { token: '' },
  setAuthData: () => {},
};

const AuthContext = createContext<AuthContextType>(defaultValue);

const logAuthRoleFallback = (response: {
  email?: string;
  role?: string;
  effectiveRole?: EffectiveRole | null;
}) => {
  if (response.effectiveRole || !response.role) {
    return;
  }

  ErrorLoggerService.logUnhandledError({
    error_type: 'AUTH_ROLE_FALLBACK',
    message: 'Auth bootstrap fell back to legacy comparable role.',
    stacktrace: JSON.stringify({
      email: response.email ?? '',
      role: response.role,
    }),
  });
};

export default function AuthProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const store = useAppStore();
  const dispatch = useAppDispatch();

  const loggedIn = useAppSelector(() => store.getState().isLoggedIn.status);
  const userRole: role = useAppSelector(() => store.getState().isLoggedIn.role);
  const effectiveRole: EffectiveRole | null = useAppSelector(
    () => store.getState().isLoggedIn.effectiveRole,
  );
  const activeView = useAppSelector(() => store.getState().isLoggedIn.activeView);

  const [authData, setAuthData] = useState<AuthData>(defaultValue.authData);

  useEffect(() => {
    dispatch(setisLoggedIn(AuthService.isLoggedIn()));
  }, [dispatch]);

  useEffect(() => {
    if (!loggedIn) return;

    const syncUserCourses = async () => {
      try {
        await UserCourseStorageService.retrieveCourseCookie();
      } catch (error) {
        console.error('Failed to refresh user course access state.', error);
      }
    };

    syncUserCourses();
  }, [loggedIn]);

  const failClosedAuthBootstrap = useCallback(() => {
    AuthService.signOutReset();
    dispatch(setisLoggedIn(false));
    dispatch(setRole(''));
    dispatch(setEffectiveRole(null));
    dispatch(setActiveView(null));
    dispatch(setUserFirstName('Student'));
    dispatch(setUserLastName(''));
    dispatch(setUserEmail(''));
    router.push('/');
  }, [dispatch, router]);

  const getUserInfo = useCallback(async () => {
    try {
      const response = await (await Request.get('user/getUserBasicInfo'))?.json();
      if (!response || response.error || (!response.role && !response.effectiveRole)) {
        failClosedAuthBootstrap();
        return;
      }

      const nextEffectiveRole = (response.effectiveRole ?? null) as EffectiveRole | null;
      logAuthRoleFallback({
        email: response.email,
        role: response.role,
        effectiveRole: nextEffectiveRole,
      });
      dispatch(setEffectiveRole(nextEffectiveRole));
      const comparableRole = nextEffectiveRole
        ? EffectiveRoleService.deriveLegacyComparableRole(nextEffectiveRole)
        : ((response.role as role) ?? '');
      if (!comparableRole) {
        failClosedAuthBootstrap();
        return;
      }

      dispatch(setRole(comparableRole));
      dispatch(setUserFirstName(response.first_name));
      dispatch(setUserLastName(response.last_name));
      dispatch(setUserEmail(response.email));

      const defaultView = HomeViewAccessService.resolveDefaultView(
        nextEffectiveRole,
        comparableRole,
      );
      if (!defaultView) {
        failClosedAuthBootstrap();
        return;
      }
      dispatch(setActiveView(defaultView));
      return;
    } catch {
      failClosedAuthBootstrap();
      return;
    }
  }, [dispatch, failClosedAuthBootstrap]);

  useEffect(() => {
    if (loggedIn && userRole == '') {
      getUserInfo();
    }
  }, [loggedIn, userRole, getUserInfo]);

  useEffect(() => {
    if (userRole !== '') {
      const normalizePath = (path: string): string => {
        if (!path) return '/';
        const cleaned = path.split('#')[0]?.split('?')[0]?.trim() ?? '/';
        if (cleaned.length > 1 && cleaned.endsWith('/')) {
          return cleaned.slice(0, -1);
        }
        return cleaned || '/';
      };

      const activeViewInStore = activeView;
      if (activeViewInStore === null) return;
      const availableViews = HomeViewAccessService.getAvailableViews(effectiveRole, userRole);
      if (!availableViews.includes(activeViewInStore)) {
        const nextView = HomeViewAccessService.resolveDefaultView(effectiveRole, userRole);
        if (nextView && nextView !== activeViewInStore) {
          dispatch(setActiveView(nextView));
          router.replace(nextView === 'student' ? '/student' : '/admin');
        }
        return;
      }
      const roleForPathCheck = (
        activeViewInStore === 'admin' ? 'enterprise_admin' : userRole
      ) as role;
      const validPath = isPathPermittedForRole(pathname, roleForPathCheck, effectiveRole);

      const portalRoot = EffectiveRoleService.resolvePortalRoot(effectiveRole, userRole);
      const isActualStudentRole = userRole === 'student' || effectiveRole?.roleType === 'student';
      const studentPortalAllowed =
        isActualStudentRole &&
        activeViewInStore === 'student' &&
        (pathname === '/student' || pathname.startsWith('/student/'));
      const onExpectedPortal =
        pathname === portalRoot || pathname.startsWith(`${portalRoot}/`) || studentPortalAllowed;
      if (!onExpectedPortal) {
        router.push(portalRoot);
        return;
      }

      if (
        normalizePath(pathname) === '/admin/institute-overview' &&
        activeViewInStore !== 'admin'
      ) {
        router.replace('/admin');
        return;
      }

      // If no valid path is found, redirect to the first route available for this role
      if (!validPath && !studentPortalAllowed) {
        const targetRoute = resolveFirstPermittedRoute(userRole, effectiveRole);
        if (!targetRoute) {
          return;
        }

        if (normalizePath(pathname) !== normalizePath(targetRoute)) {
          router.push(targetRoute);
        }
      }
    }
  }, [userRole, effectiveRole, pathname, router, activeView, dispatch]);

  return <AuthContext.Provider value={{ authData, setAuthData }}>{children}</AuthContext.Provider>;
}

export { AuthProvider, AuthContext };
